import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, concat } from 'rxjs';
import { ApiService } from '../api/api.service';
import { PermissionsService } from '../permissions/permissions.service';
import moment from 'moment-timezone';
import { tap } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class RequestsPulseService {
  private requestsPulseCache = new Map<string, { data: any, timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  org_uid = localStorage.getItem('org_uid');

  path = "check/pulses/checks"

  statusMap = {
    'Invited': 0,
    'Declined': 2,
    'In progress': 4,
    'Submitted': 5,
    'Verified': 6,
    'Expired': 3,
    'Scheduled': 0,
    'Sending': 2,
    'pulseInprogress': 3,
    'Completed': 5,
    'Stopped': 1,
    'Cancelled': 7

  }

  constructor(
    private apiService: ApiService,
    private permissionsService: PermissionsService
  ) { }

  getAllRequests(): Observable<any> {
    const path = this.path;
    return this.apiService.get(path);
  }

  getRequests(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any): Observable<any>{
    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    if (searchOptions) {
      searchOptions['columns'].forEach(element => {
        params = params.set(element, searchOptions['value'])
      });
    }
    if (sortString){
      params = params.set('sort',sortString);
    }
    if (filterOptions) {
      if(filterOptions['filter'] != 'All'){
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
                  .set('start_date', utcStart.format())
                  .set('end_date', utcEnd.format());
      }
      if(filterOptions['checkboxOption'].length){
        params = params.set('status', filterOptions['checkboxOption'].map(status=>this.statusMap[status]).join(','));
      }
      params = params.set('refine_by', filterOptions['radioOption']);
    }
    const path = this.path;
    return this.apiService.get(path, {params});
  }

  getRequestsDuplicateParams(searchOptions?:any, rowsPerPage=0, pageNumber=1, sortString?: string, filterOptions?:any, userPermitted: any = false, refine_by?: any): Observable<any>{
    const cacheKey = this.createCacheKey(searchOptions, rowsPerPage, pageNumber, sortString, filterOptions, userPermitted, refine_by);
    const isCacheEnabled = this.permissionsService.isFeatureActivated('ent-9003-fe-requests-persistent-data');

    let params = new HttpParams();
    if (rowsPerPage){
      params = params.set('limit', String(rowsPerPage));
      params = params.set('offset', String((pageNumber-1)*rowsPerPage))
    }
    if (searchOptions) {
      searchOptions.forEach(element => {
        params = params.append(element.key.toLowerCase(), element.value);
      });
    }
    if (sortString){
      params = params.set('sort',sortString);
    }
    if (filterOptions) {
      if(filterOptions['filter'] != 'All'){
        let utcStart = moment(filterOptions['start_date'], "YYYY-MM-DD").utc();
        let utcEnd = moment(filterOptions['end_date'], "YYYY-MM-DD").utc();
        params = params.set('filter', filterOptions['filterBy'].toLowerCase().replace(' ', '_'))
                  .set('start_date', utcStart.format())
                  .set('end_date', utcEnd.format());
      }
      if(filterOptions['statusOption'].length){
        params = params.set('status', filterOptions['statusOption'].map(status=>this.statusMap[status]).join(','));
      }
      params = params.set('refine_by', filterOptions['checkboxOption'][0]);
    }
    if (userPermitted) {
      params = params.set('user_permitted', userPermitted)
    }
    if (refine_by) {
      params = params.append('refine_by', refine_by);
    }
    const path = this.path;
    
    // Create the API call observable
    const apiCall$ = this.apiService.get(path, {params}).pipe(
      tap(data => {
        if (isCacheEnabled) {
          this.requestsPulseCache.set(cacheKey, {
            data: data,
            timestamp: Date.now()
          });
        }
      })
    );

    // Stale-while-revalidate: return cached data first, then fresh data
    if (isCacheEnabled && this.requestsPulseCache.has(cacheKey)) {
      const cached = this.requestsPulseCache.get(cacheKey);
      if (cached) {
        return concat(of(cached.data), apiCall$);
      }
    }

    // No cache - just make API call
    return apiCall$;
  }

  clearCache() {
    this.requestsPulseCache.clear();
  }

  private createCacheKey(search: any, rows: number, page: number, sort: string | undefined, filter: any, userPermitted: any, refine: any): string {
    return JSON.stringify({ search, rows, page, sort, filter, userPermitted, refine });
  }

  deleteOmniRequest(requestUid){
    const path = "check/" + requestUid;
    return this.apiService.delete(path);
  }

  cancelOmniRequest(requestUid){
    const payload = {"state": 'Cancelled'}
    const path = "check/" + requestUid;
    return this.apiService.put(path, payload);
  }

  stopOmniRequest(requestUid){
    const payload = {"state": 'Stopped'}
    const path = "check/" + requestUid;
    return this.apiService.put(path, payload);
  }

  getCheckPulse(survey: any, department: any, check: any, start?: any, end?: any): Observable<any> {
    let path = "checks/pulse/checks"
    if (check.length || department.length || survey.length || start || end) {
      path = "checks/pulse/checks?";
      if (check.length) {
        path += "check=" + check;
      }
      if (survey.length) {
        if (check.length) {
          path += "&survey=" + survey;
        } else {
          path += "survey=" + survey;
        }
      }
      if (department.length) {
        if (check.length || survey.length) {
          path += "&department=" + department;
        } else {
          path += "department=" + department;
        }
      }
      if (start) {
        let utcStart = moment(start).utc();
        if (check.length || survey.length || department.length) {
          path += "&start=" + utcStart.format();
        } else {
          path += "start=" + utcStart.format();
        }
      }
      if (end) {
        let utcEnd = moment(end).utc();
        if (check.length || survey.length || department.length || start) {
          path += "&end=" + utcEnd.format();
        } else {
          path += "end=" + utcEnd.format();
        }
      }
    }
    return this.apiService.get(path);
  }
}
